import { View, StyleSheet, Pressable, ActivityIndicator } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { ViewMode } from "./types";

interface FilesHeaderProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onUpload?: () => void;
  uploading?: boolean;
}

const VIEW_OPTIONS: { mode: ViewMode; icon: string; label: string }[] = [
  { mode: "icons", icon: "view-grid", label: "Icons" },
  { mode: "list", icon: "view-list", label: "List" },
  { mode: "columns", icon: "view-sequential", label: "Columns" },
];

export function FilesHeader({ viewMode, onViewModeChange, onUpload, uploading }: FilesHeaderProps) {
  const { colors } = useTheme();

  return (
    <View style={styles.container}>
      {/* Upload Button */}
      {onUpload && (
        <Pressable
          onPress={onUpload}
          disabled={uploading}
          style={[styles.uploadButton, { backgroundColor: colors.primary, opacity: uploading ? 0.7 : 1 }]}
        >
          {uploading ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <MaterialCommunityIcons name="upload" size={18} color="#fff" />
          )}
          <ThemedText style={styles.uploadText}>{uploading ? "Uploading..." : "Upload"}</ThemedText>
        </Pressable>
      )}

      {/* Spacer */}
      <View style={styles.spacer} />

      {/* View Mode Label */}
      <ThemedText style={[styles.viewModeLabel, { color: colors.textSecondary }]}>
        {VIEW_OPTIONS.find((o) => o.mode === viewMode)?.label}
      </ThemedText>

      {/* View Toggle */}
      <View style={[styles.viewToggle, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}>
        {VIEW_OPTIONS.map((option) => {
          const isActive = viewMode === option.mode;
          return (
            <Pressable
              key={option.mode}
              onPress={() => onViewModeChange(option.mode)}
              style={[
                styles.viewOption,
                isActive && { backgroundColor: colors.primary },
                !isActive && { backgroundColor: "transparent" },
              ]}
            >
              <MaterialCommunityIcons
                name={option.icon as any}
                size={18}
                color={isActive ? "#fff" : colors.textSecondary}
              />
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
    gap: 12,
  },
  uploadButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 6,
    gap: 6,
  },
  uploadText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: "500",
  },
  spacer: {
    flex: 1,
  },
  viewModeLabel: {
    fontSize: 13,
    marginRight: 4,
  },
  viewToggle: {
    flexDirection: "row",
    borderRadius: 6,
    borderWidth: StyleSheet.hairlineWidth,
    padding: 2,
    gap: 2,
  },
  viewOption: {
    padding: 6,
    borderRadius: 4,
  },
});
